import type { ScriptLine } from "../../coldcompute";

// Caption lines are SCENE-LOCAL (fromSec resets to 0 inside each block's <Sequence>).
// FIRST-CUT TIMINGS: estimated proportionally against the real VO clips
// (public/vo/47/) — nudge fromSec after listening in Studio. Captions are
// trimmed key lines of the recorded v4 script, not full transcription.

export const HOOK: ScriptLine[] = [
  { scene: "hook", fromSec: 0.5, holdSec: 8, caption: "Elon says apps and websites are gone in six years. I was ready to ignore it." },
  { scene: "hook", fromSec: 8.5, holdSec: 6, caption: "Then Salesforce reported $1.5B in revenue from basically that exact future." },
  { scene: "hook", fromSec: 14.5, holdSec: 4, caption: "And Stripe went and bought OpenRouter." },
  { scene: "hook", fromSec: 18.5, holdSec: 6, caption: "I'd been building for this for months without fully clocking it." },
  { scene: "hook", fromSec: 24.5, holdSec: 9, caption: "If your AI product is still 'an app people go to' — that's about to become a liability." },
  { scene: "hook", fromSec: 34, holdSec: 6, caption: "The receipts, and what I'm doing about it. Let's dive in." },
];

export const S1SETUP: ScriptLine[] = [
  { scene: "s1setup", fromSec: 0.4, holdSec: 6, caption: "What actually happened this week — four receipts." },
  { scene: "s1setup", fromSec: 6.4, holdSec: 5.4, caption: "Companies with way more money than us, all making the same bet." },
];

export const S1TENSION: ScriptLine[] = [
  { scene: "s1tension", fromSec: 0.4, holdSec: 8, caption: "Claudeforce: Salesforce's data + 37 governed sales skills — inside Claude." },
  { scene: "s1tension", fromSec: 8.6, holdSec: 8.5, caption: "The biggest CRM on earth deciding its own interface is optional." },
];

export const S1BROLL: ScriptLine[] = [
  { scene: "s1broll", fromSec: 0.4, holdSec: 6, caption: "Agentforce: reportedly $1.5B in ARR." },
  { scene: "s1broll", fromSec: 6.6, holdSec: 5.5, caption: "The wider AI + data line: somewhere near $4B." },
  { scene: "s1broll", fromSec: 12.4, holdSec: 10.5, caption: "3.2 BILLION 'Agentic Work Units' — up 97% in a single quarter." },
];

export const S1ONSCREEN: ScriptLine[] = [
  { scene: "s1onscreen", fromSec: 0.4, holdSec: 8, caption: "Anthropic and OpenAI — competitors — ratified one standard in January: MCP Apps." },
  { scene: "s1onscreen", fromSec: 8.6, holdSec: 8, caption: "An AI host can render live interactive UI served by an external tool." },
  { scene: "s1onscreen", fromSec: 17, holdSec: 8, caption: "And quietly: Stripe bought OpenRouter — the gateway AI apps route model calls through." },
  { scene: "s1onscreen", fromSec: 25.4, holdSec: 8, caption: "A payments company now owns a big chunk of the pipe agents make requests over." },
];

export const S1PAYOFF: ScriptLine[] = [
  { scene: "s1payoff", fromSec: 0.4, holdSec: 7, caption: "All four point one direction: software goes where the user already is." },
  { scene: "s1payoff", fromSec: 7.6, holdSec: 7.2, caption: "The capability survives. The front end doesn't necessarily." },
];

export const SEG2: ScriptLine[] = [
  { scene: "seg2", fromSec: 0.4, holdSec: 11, caption: "My own stuff — the part that tells you which side of this you're on." },
  { scene: "seg2", fromSec: 12, holdSec: 8, caption: "EAKC: a knowledge engine over codebases and docs, with retrieval that cites its sources." },
  { scene: "seg2", fromSec: 20.5, holdSec: 7, caption: "Months before the news, the roadmap already said three things." },
  { scene: "seg2", fromSec: 28, holdSec: 13, caption: "One: widgets you can embed in OTHER PEOPLE'S apps. Already shipped — signed tokens, sandboxed rendering." },
  { scene: "seg2", fromSec: 42, holdSec: 12, caption: "Two: widgets served over MCP. My side was live-verified on production July 2nd." },
  { scene: "seg2", fromSec: 54.5, holdSec: 11, caption: "The only blocker was Claude's host rollout. And Claudeforce just proved the pattern at scale." },
  { scene: "seg2", fromSec: 66, holdSec: 17, caption: "Three, the weird one: inbound MCP + OpenAPI, auto-documenting, flagging stale docs, raising its hand like a new hire." },
  { scene: "seg2", fromSec: 84, holdSec: 12, caption: "The endgame: EAKC as the knowledge layer other AIs plug into." },
];

export const SEG3: ScriptLine[] = [
  { scene: "seg3", fromSec: 0.4, holdSec: 11, caption: "The unsexy part: none of this works if the thing behind it makes stuff up." },
  { scene: "seg3", fromSec: 12, holdSec: 8, caption: "I audited my own production chat — and caught it fabricating." },
  { scene: "seg3", fromSec: 20.5, holdSec: 8, caption: "I asked if a feature existed. It said yes — confidently, with sources. It did not exist." },
  { scene: "seg3", fromSec: 29, holdSec: 12, caption: "Similarity search hands you the eight CLOSEST things — even when the thing itself isn't there." },
  { scene: "seg3", fromSec: 41.5, holdSec: 8, caption: "The system fails open; the model stitches adjacent facts into a confident yes." },
  { scene: "seg3", fromSec: 50, holdSec: 12, caption: "The fix: NEGATIVE EVIDENCE — no source documents it, the answer is 'it doesn't exist'. Plus evals that punish the old behaviour." },
  { scene: "seg3", fromSec: 62.5, holdSec: 12, caption: "Same discipline on that knowledge-graph paper: adopted 5, already had 4, rejected 1." },
  { scene: "seg3", fromSec: 75, holdSec: 12, caption: "One line stuck: RECENCY IS NOT APPLICABILITY. The newest fact isn't automatically the answer." },
  { scene: "seg3", fromSec: 87.5, holdSec: 11, caption: "The rule now: every change gets measured before it counts." },
];

export const SEG4: ScriptLine[] = [
  { scene: "seg4", fromSec: 0.4, holdSec: 11, caption: "Lastly — the thesis of this video showing up inside my own product. Precisely, no overclaiming." },
  { scene: "seg4", fromSec: 12, holdSec: 8, caption: "Writing this script, I needed my product-vision numbers. So I just asked EAKC." },
  { scene: "seg4", fromSec: 20.5, holdSec: 9, caption: "It answered — citing the vision document an AI session wrote YESTERDAY." },
  { scene: "seg4", fromSec: 30, holdSec: 13, caption: "Nobody uploaded it. A sync job re-reads the codebase and docs every morning at 08:00 UTC." },
  { scene: "seg4", fromSec: 43.5, holdSec: 14, caption: "Next to it: a recurring mission that checks new chunks since the last sync — and emails me a digest. A standup update." },
  { scene: "seg4", fromSec: 58, holdSec: 12, caption: "Honestly: the full 'employee' — writing docs itself, patching stale ones, asking questions — is NOT built yet." },
  { scene: "seg4", fromSec: 70.5, holdSec: 13, caption: "Draft one of this script said 'the knowledge base documented itself'. That's the exact nearness-vs-existence trap from segment three." },
  { scene: "seg4", fromSec: 84, holdSec: 10, caption: "Ingesting a doc an AI wrote is not writing it. I caught the overclaim in my own script." },
  { scene: "seg4", fromSec: 94.5, holdSec: 17, caption: "Today's embryo: re-ingests every morning, notices its changes, emails me. ~70% of the vision exists. The rest is a list." },
];

export const ENDCARD: ScriptLine[] = [
  { scene: "endcard", fromSec: 0.4, holdSec: 9.5, caption: "Next video: I connect EAKC to Claude live — the test that sat blocked for eight weeks." },
];

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
